import { createContext, useContext, useMemo, useState } from "react";
import { setToken } from "../store/authStore";

export interface IPageFeature extends IPagePermission {
    features: IPagePermission[];
}

export interface IPagePermission {
    id: number,
    name: string,
    label?: string,
    url?: string,
    description?: string,
    isFeature: boolean,
    parentId?: number,
    parent?: string,
    permission: boolean
}

export interface ILogin {
    id: number,
    name: string,
    token: string,
    permissions: Array<IPagePermission>
}

interface AuthContextType {
    loggedIn: boolean,
    token?: string,
    userId?: number,
    userName: string,
    login: (data: ILogin) => void,
    logout: () => void,
    myRoutes: { label: string, url: string }[];
    hasPermission: (pageName: string, featureName?: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [loginObj, setLoginObj] = useState<ILogin | null>(() => {
        const auth = sessionStorage.getItem("loginData");
        return auth ? JSON.parse(auth) : null;
    });

    const login = (data: ILogin) => {
        sessionStorage.setItem("loginData", JSON.stringify(data));
        setToken(data.token);
        setLoginObj(data);
    };

    const logout = () => {
        sessionStorage.removeItem("loginData");
        setToken(null);
        setLoginObj(null);
    };

    const pageFeatures = useMemo(() => {
        if (!loginObj) return [];

        const features: IPageFeature[] = [];

        const parentPages = loginObj.permissions?.filter(p => !p.isFeature);

        parentPages?.forEach(pp => {
            const pageFeature: IPageFeature = {
                ...pp,
                features: []
            };

            loginObj.permissions?.forEach(p => {
                if (pp.id === p.parentId) {
                    pageFeature.features.push(p);
                }
            });

            features.push(pageFeature);
        });

        return features;
    }, [loginObj]);

    const hasPermission = (pageName: string, featureName?: string): boolean => {
        const page = pageFeatures.find(page => page.name === pageName);
        if (!page) return false;
        if (!featureName) {
            return page.permission;
        }
        return page.features.some(f => f.name === featureName && f.permission);
    };

    const loggedIn = !!loginObj;
    const userName = loginObj?.name ?? '';
    const userId = loginObj?.id ?? undefined;
    const token = loginObj?.token ?? undefined;
    const myRoutes = pageFeatures.filter(p => p.permission && p.url).map(p => ({ label: p.label!, url: p.url! }));

    return (
        <AuthContext.Provider value={{ loggedIn, token, userId, userName, myRoutes, login, logout, hasPermission }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
